import { create } from "zustand";

import {
  authApi,
  normalizeUser,
  registrationToFormData,
  User,
} from "../services/auth.api";
import { ApiError } from "../services/api";

import {
  PendingRegistration,
  RegistrationValues,
} from "../components/register/types/registration";

type AuthStoreState = {
  /* ==================== State ==================== */

  pending: PendingRegistration | null;
  verified: boolean;
  isAuthenticated: boolean;
  currentUser: User | null;
  apiError: string;
  loginLoading: boolean;
  googleLoading: boolean;
  bootstrapped: boolean; // ← جديد: يمنع redirect قبل ما /auth/me ترد

  /* ==================== Actions ==================== */

  clearApiError: () => void;
  setCurrentUser: (user: User | null) => void;
  fetchCurrentUser: () => Promise<void>;
  register: (data: RegistrationValues) => Promise<void>;
  verifyCode: (code: string) => Promise<boolean>;
  resendCode: () => Promise<void>;
  login: (email: string, password: string) => Promise<boolean>;
  googleAuth: (accessToken: string) => Promise<void>;
  logout: () => void;
  me: () => Promise<User>;
};

/* ===================== HELPERS ===================== */

function parseError(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Network error";
}

/* ===================== STORE ===================== */

export const useAuthStore = create<AuthStoreState>((set, get) => ({
  /* ==================== Initial State ==================== */

  pending: null,
  verified: false,
  isAuthenticated: false,
  currentUser: null,
  apiError: "",
  loginLoading: false,
  googleLoading: false,
  bootstrapped: false,

  /* ==================== Utilities ==================== */

  clearApiError: () => set({ apiError: "" }),

  setCurrentUser: (user) =>
    set({
      currentUser: user,
      isAuthenticated: user !== null,
    }),

  /**
   * GET /auth/me → بيرجع الـ user مباشرة (شكل:
   * { id, name, email, role, accountType, ... })
   */
  fetchCurrentUser: async () => {
    try {
      const user = await authApi.me();
      set({
        currentUser: normalizeUser(user),
        isAuthenticated: true,
      });
    } catch {
      set({
        currentUser: null,
        isAuthenticated: false,
      });
    } finally {
      set({ bootstrapped: true });
    }
  },

  /* ==================== Register ==================== */

  register: async (data) => {
    set({
      apiError: "",
      verified: false,
      isAuthenticated: false,
      currentUser: null,
    });

    try {
      const formData = registrationToFormData(data);
      const res = await authApi.register(formData); // { email, verificationCode? }

      set({
        pending: {
          email: res.email,
          code: res.verificationCode ?? "",
          payload: data,
        },
      });
    } catch (error) {
      set({ apiError: parseError(error) });
      throw error;
    }
  },

  /* ==================== Verify Code ==================== */

  verifyCode: async (code) => {
    const pending = get().pending;

    if (!pending) {
      set({ apiError: "Registration session has expired." });
      return false;
    }

    const verificationCode = code.trim();

    if (!verificationCode) {
      set({ apiError: "Please enter the verification code." });
      return false;
    }

    set({ apiError: "" });

    try {
      // res = { verified, user }
      const res = await authApi.verifyEmail(pending.email, verificationCode);

      if (!res?.user) {
        set({
          apiError:
            "Verification succeeded but the user data was not returned.",
        });
        return false;
      }

      set({
        verified: true,
        isAuthenticated: true,
        apiError: "",
        currentUser: normalizeUser(res.user),
      });

      return true;
    } catch (error) {
      set({
        apiError: parseError(error),
        verified: false,
        isAuthenticated: false,
        currentUser: null,
      });
      return false;
    }
  },

  /* ==================== Resend Code ==================== */

  resendCode: async () => {
    const pending = get().pending;

    if (!pending) {
      set({ apiError: "Registration session has expired." });
      return;
    }

    set({ apiError: "" });

    try {
      const res = await authApi.resendVerification(pending.email);

      if (res?.verificationCode) {
        set({
          pending: {
            ...pending,
            code: res.verificationCode,
          },
        });
      }
    } catch (error) {
      set({ apiError: parseError(error) });
    }
  },

  /* ==================== Login ==================== */

  login: async (email, password) => {
    set({ loginLoading: true, apiError: "" });

    try {
      // res = { user }
      const res = await authApi.login(email, password);

      set({
        isAuthenticated: true,
        verified: true,
        apiError: "",
        currentUser: res?.user ? normalizeUser(res.user) : null,
      });

      return true;
    } catch (error) {
      set({
        isAuthenticated: false,
        verified: false,
        currentUser: null,
        apiError: parseError(error),
      });
      return false;
    } finally {
      set({ loginLoading: false });
    }
  },

  /* ==================== Google Auth ==================== */

  googleAuth: async (accessToken) => {
    set({ googleLoading: true, apiError: "" });

    try {
      // res = { user, isNewUser? }
      const res = await authApi.googleAuth(accessToken);

      set({
        verified: true,
        isAuthenticated: true,
        apiError: "",
        currentUser: normalizeUser(res.user),
      });
    } catch (error) {
      set({
        apiError: parseError(error),
        isAuthenticated: false,
        currentUser: null,
      });
      throw error;
    } finally {
      set({ googleLoading: false });
    }
  },

  /* ==================== Logout ==================== */

  logout: () => {
    set({
      pending: null,
      verified: false,
      isAuthenticated: false,
      currentUser: null,
      apiError: "",
    });
  },

  /* ==================== Me ==================== */

  me: async () => {
    const user = await authApi.me(); // user مباشرة
    return normalizeUser(user);
  },
}));